import { useEffect, useMemo, useState } from 'react';
import { getExchangeFreshness, type ExchangeFreshnessEntry } from '../api/exchange';
import { getApiConnections } from '../api/exchange-sync';
import { getImportHistory, type ImportJob } from '../api/portfolio-import';
import { listWallets } from '../api/wallets';
import type { Wallet } from '../types';
import {
  buildPortfolioCoverageSummary,
  normalizeApiConnection,
  type CoverageExchangeConnection,
  type CoverageResourceStatus,
  type CoverageSummarySurface,
} from '../lib/portfolioCoverageSummary';
import {
  readSetupHandoff,
  subscribeToSetupHandoff,
  type SetupHandoffContext,
} from '../lib/setupHandoff';

interface CoverageResource<T> {
  data: T;
  status: CoverageResourceStatus;
}

interface UsePortfolioCoverageSummaryOptions {
  wallets?: CoverageResource<Wallet[]>;
  importJobs?: CoverageResource<ImportJob[]>;
  exchangeConnections?: CoverageResource<CoverageExchangeConnection[]>;
  freshness?: CoverageResource<ExchangeFreshnessEntry[]>;
  setupHandoff?: SetupHandoffContext | null;
  surface?: CoverageSummarySurface;
}

export function usePortfolioCoverageSummary(
  userId: string | null | undefined,
  options: UsePortfolioCoverageSummaryOptions = {},
) {
  const [walletsResource, setWalletsResource] = useState<CoverageResource<Wallet[]>>({ data: [], status: 'loading' });
  const [importJobsResource, setImportJobsResource] = useState<CoverageResource<ImportJob[]>>({ data: [], status: 'loading' });
  const [exchangeConnectionsResource, setExchangeConnectionsResource] = useState<CoverageResource<CoverageExchangeConnection[]>>({ data: [], status: 'loading' });
  const [freshnessResource, setFreshnessResource] = useState<CoverageResource<ExchangeFreshnessEntry[]>>({ data: [], status: 'loading' });
  const [setupHandoff, setSetupHandoff] = useState<SetupHandoffContext | null>(null);

  useEffect(() => {
    setSetupHandoff(options.setupHandoff ?? readSetupHandoff(userId));
  }, [options.setupHandoff, userId]);

  useEffect(() => {
    if (options.setupHandoff !== undefined) {
      return;
    }

    return subscribeToSetupHandoff(userId, setSetupHandoff);
  }, [options.setupHandoff, userId]);

  useEffect(() => {
    if (options.wallets) return;
    if (!userId) {
      setWalletsResource({ data: [], status: 'ready' });
      return;
    }

    let cancelled = false;
    setWalletsResource((current) => ({ data: current.data, status: 'loading' }));
    listWallets()
      .then((wallets) => {
        if (!cancelled) {
          setWalletsResource({ data: wallets, status: 'ready' });
        }
      })
      .catch(() => {
        if (!cancelled) {
          setWalletsResource((current) => ({ data: current.data, status: 'error' }));
        }
      });

    return () => {
      cancelled = true;
    };
  }, [options.wallets, userId]);

  useEffect(() => {
    if (options.importJobs) return;
    if (!userId) {
      setImportJobsResource({ data: [], status: 'ready' });
      return;
    }

    let cancelled = false;
    setImportJobsResource((current) => ({ data: current.data, status: 'loading' }));
    getImportHistory(userId)
      .then((response) => {
        if (!cancelled) {
          setImportJobsResource({ data: response.jobs ?? [], status: 'ready' });
        }
      })
      .catch(() => {
        if (!cancelled) {
          setImportJobsResource((current) => ({ data: current.data, status: 'error' }));
        }
      });

    return () => {
      cancelled = true;
    };
  }, [options.importJobs, userId]);

  useEffect(() => {
    if (options.exchangeConnections) return;
    if (!userId) {
      setExchangeConnectionsResource({ data: [], status: 'ready' });
      return;
    }

    let cancelled = false;
    setExchangeConnectionsResource((current) => ({ data: current.data, status: 'loading' }));
    getApiConnections(userId)
      .then((response) => {
        if (!cancelled) {
          setExchangeConnectionsResource({
            data: (response.connections ?? []).map(normalizeApiConnection),
            status: 'ready',
          });
        }
      })
      .catch(() => {
        if (!cancelled) {
          setExchangeConnectionsResource((current) => ({ data: current.data, status: 'error' }));
        }
      });

    return () => {
      cancelled = true;
    };
  }, [options.exchangeConnections, userId]);

  useEffect(() => {
    if (options.freshness) return;
    if (!userId) {
      setFreshnessResource({ data: [], status: 'ready' });
      return;
    }

    let cancelled = false;
    setFreshnessResource((current) => ({ data: current.data, status: 'loading' }));
    getExchangeFreshness(userId)
      .then((response) => {
        if (!cancelled) {
          setFreshnessResource({ data: response.freshness ?? [], status: 'ready' });
        }
      })
      .catch(() => {
        if (!cancelled) {
          setFreshnessResource((current) => ({ data: current.data, status: 'error' }));
        }
      });

    return () => {
      cancelled = true;
    };
  }, [options.freshness, userId]);

  const wallets = options.wallets ?? walletsResource;
  const importJobs = options.importJobs ?? importJobsResource;
  const exchangeConnections = options.exchangeConnections ?? exchangeConnectionsResource;
  const freshness = options.freshness ?? freshnessResource;

  return useMemo(
    () =>
      buildPortfolioCoverageSummary({
        wallets: wallets.data,
        walletsStatus: wallets.status,
        importJobs: importJobs.data,
        importStatus: importJobs.status,
        exchangeConnections: exchangeConnections.data,
        exchangeStatus: exchangeConnections.status,
        freshness: freshness.data,
        freshnessStatus: freshness.status,
        setupHandoff,
        surface: options.surface,
      }),
    [exchangeConnections.data, exchangeConnections.status, freshness.data, freshness.status, importJobs.data, importJobs.status, options.surface, setupHandoff, wallets.data, wallets.status],
  );
}
